import { useMemo, useRef, useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  FaBars,
  FaMoon,
  FaSignInAlt,
  FaSignOutAlt,
  FaSun,
  FaTimes,
  FaUserShield,
  FaFacebookF,
  FaInstagram,
  FaChevronDown,
  FaInfoCircle,
  FaRocket,
  FaImages,
  FaEuroSign,
  FaBookOpen,
  FaPlayCircle,
  FaHome,
  FaUsers,
  FaClock,
  FaUserPlus,
} from "react-icons/fa";

import { getSettings } from "../../../services/settingsServices";
import { getMenuPagesPersonnalisees } from "../../../services/pagesPersonnaliseesServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const parseEnvBoolean = (value) => {
  return ["1", "true", "yes", "on"].includes(
    String(value || "")
      .trim()
      .toLowerCase(),
  );
};

const IS_DEMO = parseEnvBoolean(process.env.REACT_APP_IS_DEMO);

const mainNavItems = [
  {
    label: "Accueil",
    href: "/",
    icon: FaHome,
  },
  {
    label: "Inscription",
    href: "/inscription",
    icon: FaSignInAlt,
  },
];

const clubMenuItems = [
  {
    label: "Présentation du club",
    shortLabel: "Présentation",
    href: "/club",
    icon: FaUsers,
  },
  {
    label: "Horaires & Tarifs",
    shortLabel: "Horaires & Tarifs",
    href: "/horaires-tarifs",
    icon: FaClock,
  },
];

const demoMenuItems = [
  {
    label: "Présentation",
    href: "/monappli-club-light#presentation",
    icon: FaInfoCircle,
  },
  {
    label: "Fonctionnalités",
    href: "/monappli-club-light#fonctionnalites",
    icon: FaRocket,
  },
  {
    label: "Captures d’écran",
    href: "/monappli-club-light#screens",
    icon: FaImages,
  },
  {
    label: "Tarifs",
    href: "/monappli-club-light#tarifs",
    icon: FaEuroSign,
  },
];

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const getUserInitials = (user) => {
  const prenom = user?.prenom || user?.first_name || "";
  const nom = user?.nom || user?.last_name || "";

  const initials = `${prenom.charAt(0)}${nom.charAt(0)}`.trim();

  if (initials) {
    return initials.toUpperCase();
  }

  if (user?.email) {
    return user.email.charAt(0).toUpperCase();
  }

  return "U";
};

function UserAvatar({ user }) {
  const initials = useMemo(() => getUserInitials(user), [user]);

  const photoUrl = getImageUrl(
    user?.photo_url || user?.photo || user?.avatar || user?.image_url,
  );

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={`${user?.prenom || ""} ${user?.nom || ""}`}
        className="h-11 w-11 rounded-full object-cover"
      />
    );
  }

  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
      {initials}
    </div>
  );
}

export default function Navbar({ user: contextUser }) {
  const [open, setOpen] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [openClubMenu, setOpenClubMenu] = useState(false);
  const [openDemoMenu, setOpenDemoMenu] = useState(false);
  const [theme, setTheme] = useState("light");
  const [localUser, setLocalUser] = useState(() => getStoredUser());
  const [forcedLoggedOut, setForcedLoggedOut] = useState(false);
  const [openPagesMenu, setOpenPagesMenu] = useState(false);
  const [customPages, setCustomPages] = useState([]);

  const [clubInfo, setClubInfo] = useState({
    logo: "",
    nom: "MonAppliClub Light",
    facebook: "",
    instagram: "",
  });

  const navigate = useNavigate();
  const location = useLocation();

  const userMenuRef = useRef(null);
  const clubMenuRef = useRef(null);
  const demoMenuRef = useRef(null);
  const pagesMenuRef = useRef(null);

  const user = forcedLoggedOut ? null : localUser || contextUser || null;
  const isAuthenticated = Boolean(user);

  const logoUrl = getImageUrl(clubInfo.logo);

  const isClubPage = ["/club", "/horaires-tarifs"].includes(location.pathname);
  const isCustomPage = location.pathname.startsWith("/pages/");
  const isDemoPage = location.pathname.startsWith("/monappli-club-light");

  useEffect(() => {
    const fetchCustomPages = async () => {
      try {
        const pages = await getMenuPagesPersonnalisees();
        setCustomPages(Array.isArray(pages) ? pages : []);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des pages personnalisées :",
          error,
        );
        setCustomPages([]);
      }
    };

    fetchCustomPages();
  }, []);

  useEffect(() => {
    const fetchClubSettings = async () => {
      try {
        const settings = await getSettings();
        const club = Array.isArray(settings) ? settings[0] : settings;

        setClubInfo({
          logo: club?.logo_club || "",
          nom: club?.nom_club || "MonAppliClub Light",
          facebook: club?.facebook_club || "",
          instagram: club?.instagram_club || "",
        });

        if (club?.couleur_principale || club?.primary_color) {
          document.documentElement.style.setProperty(
            "--primary-color",
            club.couleur_principale || club.primary_color,
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des paramètres du club :",
          error,
        );
      }
    };

    fetchClubSettings();
  }, []);

  useEffect(() => {
    if (!forcedLoggedOut && contextUser) {
      setLocalUser(contextUser);
    }
  }, [contextUser, forcedLoggedOut]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia?.(
      "(prefers-color-scheme: dark)",
    )?.matches;

    const initialTheme = savedTheme || (prefersDark ? "dark" : "light");

    setTheme(initialTheme);
    document.documentElement.classList.toggle("dark", initialTheme === "dark");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setOpenUserMenu(false);
      }

      if (clubMenuRef.current && !clubMenuRef.current.contains(event.target)) {
        setOpenClubMenu(false);
      }

      if (demoMenuRef.current && !demoMenuRef.current.contains(event.target)) {
        setOpenDemoMenu(false);
      }
      if (
        pagesMenuRef.current &&
        !pagesMenuRef.current.contains(event.target)
      ) {
        setOpenPagesMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenUserMenu(false);
    setOpenClubMenu(false);
    setOpenPagesMenu(false);
    setOpenDemoMenu(false);
  }, [location.pathname, location.hash]);

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";

    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    document.documentElement.classList.toggle("dark", nextTheme === "dark");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");

    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userInfo");

    setLocalUser(null);
    setForcedLoggedOut(true);
    setOpen(false);
    setOpenUserMenu(false);
    setOpenClubMenu(false);
    setOpenDemoMenu(false);
    setOpenPagesMenu(false);
    navigate("/", {
      replace: true,
    });
  };

  const handleCloseAllMenus = () => {
    setOpen(false);
    setOpenUserMenu(false);
    setOpenClubMenu(false);
    setOpenDemoMenu(false);
    setOpenPagesMenu(false);
  };

  const handleGoToDemoDashboard = () => {
    handleCloseAllMenus();

    navigate("/appli/dashboard", {
      state: {
        demo: true,
      },
    });
  };

  const renderNavItem = (item, mobile = false) => {
    const Icon = item.icon;

    const baseClass = mobile
      ? "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
      : "text-sm font-semibold text-slate-700 transition hover:text-primary dark:text-slate-200 dark:hover:text-orange-400";

    return (
      <NavLink
        key={item.label}
        to={item.href}
        onClick={handleCloseAllMenus}
        className={({ isActive }) =>
          `${baseClass} ${isActive ? "text-primary dark:text-orange-400" : ""}`
        }
      >
        {mobile && Icon && (
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <Icon />
          </span>
        )}
        {item.label}
      </NavLink>
    );
  };

  const renderClubDesktopMenu = () => {
    return (
      <div className="relative" ref={clubMenuRef}>
        <button
          type="button"
          onClick={() => setOpenClubMenu((prev) => !prev)}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition ${
            isClubPage
              ? "bg-slate-900 text-white shadow-sm dark:bg-orange-500"
              : "text-slate-700 hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
          }`}
        >
          Le Club
          <FaChevronDown
            className={`text-xs transition ${openClubMenu ? "rotate-180" : ""}`}
          />
        </button>

        {openClubMenu && (
          <div className="absolute left-1/2 top-[calc(100%+14px)] w-72 -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-950">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                Informations du club
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Présentation, horaires, tarifs et informations pratiques.
              </p>
            </div>

            <div className="flex flex-col p-3">
              {clubMenuItems.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={handleCloseAllMenus}
                    className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      <Icon />
                    </span>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPagesDesktopMenu = () => {
    if (!customPages.length) return null;

    return (
      <div className="relative" ref={pagesMenuRef}>
        <button
          type="button"
          onClick={() => setOpenPagesMenu((prev) => !prev)}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition ${
            isCustomPage
              ? "bg-slate-900 text-white shadow-sm dark:bg-orange-500"
              : "text-slate-700 hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
          }`}
        >
          Pages
          <FaChevronDown
            className={`text-xs transition ${openPagesMenu ? "rotate-180" : ""}`}
          />
        </button>

        {openPagesMenu && (
          <div className="absolute left-1/2 top-[calc(100%+14px)] w-80 -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-950">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                Pages du club
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Historique, informations, règlements, projets ou contenus
                personnalisés.Vous pouvez gérer vos propres pages
                personnalisées.
              </p>
            </div>

            <div className="flex max-h-[420px] flex-col overflow-y-auto p-3">
              {customPages.map((page) => (
                <Link
                  key={page.id || page.slug}
                  to={`/pages/${page.slug}`}
                  onClick={handleCloseAllMenus}
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600 dark:bg-cyan-950/40 dark:text-cyan-300">
                    <FaBookOpen />
                  </span>

                  <span className="min-w-0">
                    <span className="block truncate">{page.titre}</span>
                    {page.introduction && (
                      <span className="mt-0.5 line-clamp-1 block text-xs font-medium text-slate-400 dark:text-slate-500">
                        {page.introduction}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDemoDesktopMenu = () => {
    if (!IS_DEMO) return null;

    return (
      <div className="relative" ref={demoMenuRef}>
        <button
          type="button"
          onClick={() => setOpenDemoMenu((prev) => !prev)}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition ${
            isDemoPage
              ? "bg-primary text-white shadow-sm dark:bg-orange-500"
              : "text-slate-700 hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
          }`}
        >
          Application
          <FaChevronDown
            className={`text-xs transition ${openDemoMenu ? "rotate-180" : ""}`}
          />
        </button>

        {openDemoMenu && (
          <div className="absolute left-1/2 top-[calc(100%+14px)] w-80 -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-950">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                MonAppliClub Light
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Présentation commerciale, fonctionnalités, tarifs, captures et
                tutoriel.
              </p>
            </div>

            <div className="flex flex-col p-3">
              {demoMenuItems.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={handleCloseAllMenus}
                    className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300">
                      <Icon />
                    </span>
                    {item.label}
                  </Link>
                );
              })}

              <button
                type="button"
                onClick={handleGoToDemoDashboard}
                className="mt-2 flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-left text-sm font-bold text-primary transition hover:bg-primary hover:text-white dark:border-orange-400/30 dark:bg-orange-500/10 dark:text-orange-300 dark:hover:bg-orange-500 dark:hover:text-white"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-primary shadow-sm dark:bg-slate-950 dark:text-orange-300">
                  <FaPlayCircle />
                </span>
                Voir la démo du Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderClubMobileMenu = () => {
    return (
      <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <p className="mb-2 px-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
          Le Club
        </p>

        <div className="flex flex-col gap-2">
          {clubMenuItems.map((item) => {
            const Icon = item.icon;

            return (
              <Link
                key={item.label}
                to={item.href}
                onClick={handleCloseAllMenus}
                className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <Icon />
                </span>
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPagesMobileMenu = () => {
    if (!customPages.length) return null;

    return (
      <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <p className="mb-2 px-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
          Pages
        </p>

        <div className="flex flex-col gap-2">
          {customPages.map((page) => (
            <Link
              key={page.id || page.slug}
              to={`/pages/${page.slug}`}
              onClick={handleCloseAllMenus}
              className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600 dark:bg-cyan-950/40 dark:text-cyan-300">
                <FaBookOpen />
              </span>

              <span className="min-w-0">
                <span className="block truncate">{page.titre}</span>
                {page.introduction && (
                  <span className="mt-0.5 line-clamp-1 block text-xs font-medium text-slate-400 dark:text-slate-500">
                    {page.introduction}
                  </span>
                )}
              </span>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const renderDemoMobileMenu = () => {
    if (!IS_DEMO) return null;

    return (
      <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <div className="mb-3 rounded-3xl bg-orange-50 p-4 dark:bg-orange-950/20">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            MonAppliClub Light
          </p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
            Informations, tarifs, fonctionnalités, captures d’écran et tutoriel
            de l’application.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {demoMenuItems.map((item) => {
            const Icon = item.icon;

            return (
              <Link
                key={item.label}
                to={item.href}
                onClick={handleCloseAllMenus}
                className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300">
                  <Icon />
                </span>
                {item.label}
              </Link>
            );
          })}

          <button
            type="button"
            onClick={handleGoToDemoDashboard}
            className="mt-2 flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-left text-sm font-bold text-primary transition hover:bg-primary hover:text-white dark:border-orange-400/30 dark:bg-orange-500/10 dark:text-orange-300 dark:hover:bg-orange-500 dark:hover:text-white"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-primary shadow-sm dark:bg-slate-950 dark:text-orange-300">
              <FaPlayCircle />
            </span>
            Voir la démo du Dashboard
          </button>
        </div>
      </div>
    );
  };

  const userFullName =
    `${user?.prenom || ""} ${user?.nom || ""}`.trim() ||
    user?.nom_complet ||
    user?.name ||
    user?.email ||
    "Utilisateur";

  const userRole =
    user?.role?.nom_role ||
    user?.role_name ||
    user?.role ||
    (user?.role_id === 1 ? "Administrateur" : "Utilisateur");

  return (
    <header className="fixed top-0 z-50 w-full border-b border-slate-200/70 bg-white/85 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/85">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 lg:px-8">
        <Link
          to="/"
          onClick={handleCloseAllMenus}
          className="flex min-w-0 flex-1 items-center gap-3 pr-2 lg:flex-none lg:pr-0"
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700 sm:h-16 sm:w-16">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`Logo ${clubInfo.nom}`}
                className="h-full w-full object-contain p-1"
              />
            ) : (
              <span className="font-barlow text-xl font-bold text-primary">
                MC
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate font-poppins text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-[13px]">
              {IS_DEMO ? "MonAppliClub Light" : "Espace Club"}
            </p>

            <p className="block w-full truncate font-poppins text-[17px] font-extrabold text-slate-900 dark:text-white sm:text-[18px] lg:max-w-[260px]">
              {clubInfo.nom}
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-4 lg:flex xl:gap-6">
          {mainNavItems.slice(0, 1).map((item) => renderNavItem(item))}
          {renderClubDesktopMenu()}
          {renderPagesDesktopMenu()}
          {mainNavItems.slice(1).map((item) => renderNavItem(item))}
          {renderDemoDesktopMenu()}
        </nav>

        <div className="flex items-center gap-3">
          {clubInfo.facebook && (
            <button
              type="button"
              onClick={() =>
                window.open(clubInfo.facebook, "_blank", "noopener,noreferrer")
              }
              className="hidden h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm transition hover:text-blue-600 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-white lg:flex"
              aria-label="Facebook"
            >
              <FaFacebookF />
            </button>
          )}

          {clubInfo.instagram && (
            <button
              type="button"
              onClick={() =>
                window.open(clubInfo.instagram, "_blank", "noopener,noreferrer")
              }
              className="hidden h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm transition hover:text-pink-600 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-white lg:flex"
              aria-label="Instagram"
            >
              <FaInstagram />
            </button>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            aria-label="Changer le thème"
          >
            {theme === "dark" ? <FaSun /> : <FaMoon />}
          </button>

          <div className="relative hidden lg:block" ref={userMenuRef}>
            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => setOpenUserMenu((prev) => !prev)}
                  className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-900"
                >
                  <UserAvatar user={user} />

                  <div className="text-left leading-tight">
                    <p className="max-w-[170px] truncate text-sm font-bold text-slate-900 dark:text-white">
                      {userFullName}
                    </p>
                    <p className="max-w-[170px] truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                      {userRole}
                    </p>
                  </div>
                </button>

                {openUserMenu && (
                  <div className="absolute right-0 top-[calc(100%+12px)] w-72 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                    <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                      <div className="flex items-center gap-3">
                        <UserAvatar user={user} />

                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900 dark:text-white">
                            {userFullName}
                          </p>

                          {user?.email && (
                            <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                              {user.email}
                            </p>
                          )}

                          <p className="mt-1 inline-flex rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
                            {userRole}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col p-3">
                      {Number(user?.role_id) === 1 && (
                        <Link
                          to="/appli/dashboard"
                          onClick={handleCloseAllMenus}
                          className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaUserShield />
                          Tableau de bord
                        </Link>
                      )}

                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:hover:bg-red-950/30"
                      >
                        <FaSignOutAlt />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  onClick={handleCloseAllMenus}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                >
                  <FaSignInAlt />
                  Connexion
                </Link>

                <Link
                  to="/register"
                  onClick={handleCloseAllMenus}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow-md dark:bg-orange-500"
                >
                  <FaUserPlus />
                  Créer un compte
                </Link>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-white lg:hidden"
            aria-label="Ouvrir le menu"
          >
            {open ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-x-0 top-[88px] z-40 max-h-[calc(100dvh-88px)] overflow-y-auto overscroll-contain border-t border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950 sm:top-[96px] sm:max-h-[calc(100dvh-96px)] lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
            {mainNavItems.slice(0, 1).map((item) => renderNavItem(item, true))}

            {renderClubMobileMenu()}

            {renderPagesMobileMenu()}

            {mainNavItems.slice(1).map((item) => renderNavItem(item, true))}

            {renderDemoMobileMenu()}

            {(clubInfo.facebook || clubInfo.instagram) && (
              <div className="mt-4 flex items-center gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
                {clubInfo.facebook && (
                  <button
                    type="button"
                    onClick={() => {
                      window.open(
                        clubInfo.facebook,
                        "_blank",
                        "noopener,noreferrer",
                      );
                      handleCloseAllMenus();
                    }}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    aria-label="Facebook"
                  >
                    <FaFacebookF />
                  </button>
                )}

                {clubInfo.instagram && (
                  <button
                    type="button"
                    onClick={() => {
                      window.open(
                        clubInfo.instagram,
                        "_blank",
                        "noopener,noreferrer",
                      );
                      handleCloseAllMenus();
                    }}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </button>
                )}
              </div>
            )}

            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
              {isAuthenticated ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 rounded-3xl border border-slate-200 p-3 dark:border-slate-700">
                    <UserAvatar user={user} />

                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900 dark:text-white">
                        {userFullName}
                      </p>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                        {userRole}
                      </p>
                    </div>
                  </div>

                  {Number(user?.role_id) === 1 && (
                    <Link
                      to="/appli/dashboard"
                      onClick={handleCloseAllMenus}
                      className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaUserShield />
                      Tableau de bord
                    </Link>
                  )}

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:hover:bg-red-950/30"
                  >
                    <FaSignOutAlt />
                    Déconnexion
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/login"
                    onClick={handleCloseAllMenus}
                    className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      <FaSignInAlt />
                    </span>
                    Connexion
                  </Link>

                  <Link
                    to="/register"
                    onClick={handleCloseAllMenus}
                    className="flex items-center gap-3 rounded-2xl bg-primary px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:shadow-md dark:bg-orange-500"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/15 text-white">
                      <FaUserPlus />
                    </span>
                    Créer un compte
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
